import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
    NavigateContext,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Account CRUD page in a tab.
 */
const ACCOUNT_CRUD_ROUTE = "/home/accounts/accountConfigCrud";

//endregion

//region Component

/**
 * Renders the Accounts configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Accounts MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - keep the selected tab title synchronized with the current language.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {boolean} props.refresh - External refresh flag.
 * @param {Function} props.setRefresh - External refresh setter.
 * @returns {JSX.Element} Accounts configuration page.
 */
export default function AccountConfig({
    roles,
    onCreateTab,
    toastMessages,
    refresh,
    setRefresh,
}) {
    const ctx = useContext(LoadingContext);
    const { renameTab } = useContext(NavigateContext);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const selectedTab = useSelector(
        (state) =>
            state.StateKeyValue.find((obj) => obj.key === "selectedTab")?.value
    );

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Print is not part of the desired Accounts workflow;
     * - Copy is disabled to keep account creation explicit.
     */
    const removeContextItems = useMemo(() => ["Print", "Copy"], []);

    /**
     * Keeps the current tab label aligned with the active language.
     */
    useEffect(() => {
        if (!selectedTab) return;

        renameTab(selectedTab, t("AccountConfig"));
    }, [renameTab, selectedTab, t]);

    /**
     * Opens the Account CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected account row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected account row.
     * @returns {void}
     */
    const openAccountCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "AccountLine",
                        value: row,
                    })
                );
            }

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewAccount")
                    : operation === "View"
                        ? `${t("LabelViewAccount")} "${row?.Name ?? ""}"`
                        : `${t("LabelEditAccount")} "${row?.Name ?? ""}"`;

            onCreateTab(ACCOUNT_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Account workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openAccountCrud("View", row)],
                ["Create", () => openAccountCrud("Create")],
                ["Edit", (row) => openAccountCrud("Edit", row)],
            ]),
        [openAccountCrud]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("Accounts")}
                </div>

                <div className="config-page__description">
                    {t("InfoAccounts")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Accounts"
                    roleName="Accounts"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

AccountConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    refresh: PropTypes.bool,
    setRefresh: PropTypes.func,
};

//endregion